import { Popover } from '@bambi/component-library/src/components/Popover/Popover';
import { BellIcon } from '@radix-ui/react-icons';
import { useCallback } from 'react';

import { NotificationList } from './NotificationList';
import { useFeature } from 'src/app/FeatureToggle';
import { useAppDispatch } from 'src/app/store/hooks';
import {
  bambiApi,
  useNotificationsNotificationsCountRetrieveQuery,
  useNotificationsNotificationsMarkAllSeenPartialUpdateMutation,
} from 'src/common/external/bambi-api/notificationsApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

const NOTIFICATION_COUNT_POLLING_INTERVAL = 30 * 1000;

export function Notifications() {
  const dispatch = useAppDispatch();
  const { isLoading: isLoadingFeature, isEnabled } =
    useFeature('notifications');

  const { data: notificationCountData } =
    useNotificationsNotificationsCountRetrieveQuery(
      {},
      {
        pollingInterval: NOTIFICATION_COUNT_POLLING_INTERVAL,
        skip: !isEnabled,
      }
    );

  const [markAllSeen] =
    useNotificationsNotificationsMarkAllSeenPartialUpdateMutation();

  const handleMarkAllSeen = useCallback(async () => {
    try {
      await markAllSeen({}).unwrap();
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Unable to mark all notifications as seen',
          description: formatServerError(e),
        })
      );
    }
  }, [dispatch, markAllSeen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dispatch(bambiApi.util.invalidateTags(['notifications']));
      }
    },
    [dispatch]
  );

  const count = notificationCountData?.count ?? 0;

  if (isLoadingFeature || !isEnabled) {
    return null;
  }

  return (
    <Popover
      onOpenChange={handleOpenChange}
      trigger={
        <button
          type="button"
          className="focus:ring-indigo-500 relative rounded-full p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
          data-testid="notifications-trigger"
          aria-label={`${count} notifications`}
          onClick={handleMarkAllSeen}
        >
          <span className="sr-only">View notifications</span>
          <BellIcon
            data-testid="bell-icon"
            className="h-6 w-6"
            aria-hidden="true"
          />
          {count > 0 && (
            <span
              className="absolute right-[-8px] top-[-8px] rounded-full bg-red-600 px-2 py-1 text-xs text-white"
              role="status"
              aria-live="polite"
              data-testid="notification-count"
            >
              {count}
            </span>
          )}
        </button>
      }
      content={<NotificationList />}
    />
  );
}
